import { Link } from 'react-router-dom';
import { 
  PhoneOff, 
  Shield, 
  Video, 
  MessageSquare, 
  Mail,
  Ban,
  ArrowRight,
  ExternalLink
} from 'lucide-react';
import { initiatives, getActiveInitiatives, getFutureInitiatives } from '@/config/initiatives';

const iconMap = {
  PhoneOff,
  Shield,
  Video,
  MessageSquare,
  Mail,
  Ban,
};

export default function InitiativesPage() {
  const activeInitiatives = getActiveInitiatives();
  const futureInitiatives = getFutureInitiatives();

  return (
    <div>
      {/* Hero Section */}
      <section 
        data-testid="initiatives-hero"
        className="relative pt-28 pb-16 bg-slate-50"
      >
        <div className="container mx-auto px-4 md:px-8">
          <div className="max-w-3xl">
            <span className="inline-block text-blue-600 font-medium text-sm tracking-wide uppercase mb-3">
              Our Initiatives
            </span>
            <h1 className="text-4xl md:text-5xl font-bold text-slate-900 mb-5">
              Projects That Protect & Empower
            </h1>
            <p className="text-lg text-slate-600 leading-relaxed">
              Our technology-driven initiatives protect families from scams and fraud, 
              while generating revenue to support our educational mission.
            </p>
          </div>
        </div>
      </section>

      {/* Active Initiatives - Prominent Section */}
      <section data-testid="active-initiatives" className="py-16 bg-white">
        <div className="container mx-auto px-4 md:px-8">
          <div className="mb-10">
            <h2 className="text-3xl font-bold text-slate-900 mb-3">
              Current Initiative
            </h2>
            <p className="text-slate-600">
              Our active project making a real difference in communities.
            </p>
          </div>

          {activeInitiatives.map((initiative) => {
            const Icon = iconMap[initiative.icon];
            return (
              <div
                key={initiative.id}
                data-testid={`initiative-${initiative.id}`}
                className="bg-white border-2 border-blue-200 rounded-lg p-8 max-w-3xl"
              >
                <div className="flex items-start gap-5 mb-5">
                  <div className="w-16 h-16 bg-blue-600 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Icon className="w-8 h-8 text-white" />
                  </div>
                  <div>
                    <div className="flex items-center gap-3 mb-2">
                      <h3 className="text-2xl font-bold text-slate-900">
                        {initiative.name}
                      </h3>
                      <span className="text-xs font-medium bg-green-100 text-green-700 px-3 py-1 rounded-full">
                        Active
                      </span>
                    </div>
                    <p className="text-slate-600 leading-relaxed">
                      {initiative.description}
                    </p>
                  </div>
                </div>
                <a
                  href={initiative.url}
                  className="inline-flex items-center gap-2 text-blue-700 hover:text-blue-800 font-medium transition-colors"
                >
                  Visit Project <ExternalLink className="w-4 h-4" />
                </a>
              </div>
            );
          })}
        </div>
      </section>

      {/* Future Initiatives - Subtle Section */}
      <section data-testid="future-initiatives" className="py-16 bg-slate-50">
        <div className="container mx-auto px-4 md:px-8">
          <div className="mb-8">
            <h2 className="text-2xl font-semibold text-slate-700 mb-2">
              Future Programs
            </h2>
            <p className="text-sm text-slate-500">
              Upcoming initiatives in our development roadmap.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
            {futureInitiatives.map((initiative) => {
              const Icon = iconMap[initiative.icon];
              return (
                <div
                  key={initiative.id}
                  data-testid={`initiative-${initiative.id}`}
                  className="bg-white border border-slate-200 rounded-lg p-5"
                >
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-10 h-10 bg-slate-100 rounded-lg flex items-center justify-center">
                      <Icon className="w-5 h-5 text-slate-500" />
                    </div>
                    <div>
                      <h3 className="font-medium text-slate-700 text-sm">
                        {initiative.name}
                      </h3>
                      <span className="text-xs text-slate-400">
                        Coming Soon
                      </span>
                    </div>
                  </div>
                  <p className="text-slate-500 text-xs leading-relaxed">
                    {initiative.description}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section data-testid="initiatives-cta" className="py-16 bg-slate-800">
        <div className="container mx-auto px-4 md:px-8 text-center">
          <h2 className="text-3xl font-bold text-white mb-4">
            Have an Idea for a New Initiative?
          </h2>
          <p className="text-slate-300 max-w-2xl mx-auto mb-8">
            We're always looking for new ways to protect and empower communities. 
            Share your ideas with us.
          </p>
          <Link
            to="/contact"
            data-testid="initiatives-contact"
            className="inline-flex items-center gap-2 bg-blue-600 text-white hover:bg-blue-700 rounded-md px-6 py-3 font-medium transition-colors"
          >
            Share Your Ideas
            <ArrowRight className="w-5 h-5" />
          </Link>
        </div>
      </section>
    </div>
  );
}
